import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const DEFAULT_CENTER = [4.711, -74.0721];
const DEFAULT_ZOOM = 5;
const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

function MapClickHandler({ onPick, enabled }) {
  useMapEvents({
    click(event) {
      if (!enabled) return;
      onPick(event.latlng.lat, event.latlng.lng);
    },
  });
  return null;
}

function MapViewSync({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (!center || center[0] == null || center[1] == null) return;
    map.setView(center, zoom ?? map.getZoom());
  }, [center, zoom, map]);
  return null;
}

function DraggableMarker({ position, onDragEnd, draggable }) {
  const markerRef = useRef(null);

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (!marker) return;
        const { lat, lng } = marker.getLatLng();
        onDragEnd(lat, lng);
      },
    }),
    [onDragEnd]
  );

  if (!position) return null;

  return (
    <Marker
      draggable={Boolean(draggable)}
      eventHandlers={draggable ? eventHandlers : undefined}
      position={position}
      ref={markerRef}
    />
  );
}

function IeLocationMap({ lat, lon, onChange, readOnly = false }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);
  const debounceRef = useRef(null);

  const hasCoords = Number.isFinite(lat) && Number.isFinite(lon);
  const markerPos = hasCoords ? [lat, lon] : null;
  const mapCenter = flyTarget?.center || markerPos || DEFAULT_CENTER;
  const mapZoom = flyTarget?.zoom ?? (hasCoords ? 15 : DEFAULT_ZOOM);

  const emitChange = useCallback(
    (nextLat, nextLon) => {
      if (readOnly) return;
      if (typeof onChange === 'function') {
        onChange({ lat: nextLat, lon: nextLon });
      }
    },
    [onChange, readOnly]
  );

  useEffect(() => {
    if (readOnly || !query.trim()) {
      setResults([]);
      return undefined;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const params = new URLSearchParams({
          q: query.trim(),
          format: 'json',
          addressdetails: '0',
          limit: '6',
        });
        const response = await fetch(`${NOMINATIM_URL}?${params.toString()}`, {
          headers: {
            Accept: 'application/json',
          },
        });
        if (!response.ok) {
          setResults([]);
          return;
        }
        const data = await response.json();
        setResults(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error buscando en Nominatim:', error);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 450);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, readOnly]);

  const handleSelectResult = (item) => {
    if (readOnly) return;
    const nextLat = parseFloat(item.lat);
    const nextLon = parseFloat(item.lon);
    if (!Number.isFinite(nextLat) || !Number.isFinite(nextLon)) return;
    setFlyTarget({ center: [nextLat, nextLon], zoom: 16 });
    emitChange(nextLat, nextLon);
    setQuery(item.display_name || '');
    setResults([]);
  };

  const handlePick = (nextLat, nextLon) => {
    if (readOnly) return;
    setFlyTarget(null);
    emitChange(nextLat, nextLon);
  };

  return (
    <div className="ie-location-map">
      {!readOnly && (
        <div className="ie-location-map__search mb-2">
          <label className="form-label mb-1">Buscar lugar</label>
          <input
            type="search"
            className="form-control"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Ej: Universidad Nacional, Bogotá"
            autoComplete="off"
          />
          {searching && <div className="form-text">Buscando…</div>}
          {results.length > 0 && (
            <ul className="ie-location-map__results list-group mt-1">
              {results.map((item) => (
                <button
                  key={`${item.place_id}-${item.osm_id}`}
                  type="button"
                  className="list-group-item list-group-item-action text-start"
                  onClick={() => handleSelectResult(item)}
                >
                  {item.display_name}
                </button>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="ie-location-map__canvas">
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          scrollWheelZoom
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapViewSync center={flyTarget?.center || null} zoom={flyTarget?.zoom} />
          <MapClickHandler onPick={handlePick} enabled={!readOnly} />
          <DraggableMarker
            position={markerPos}
            onDragEnd={handlePick}
            draggable={!readOnly}
          />
        </MapContainer>
      </div>
      <div className="form-text mt-2">
        {readOnly
          ? 'Ubicación de la institución.'
          : 'Haz click en el mapa o arrastra el pin para fijar la ubicación.'}
        {hasCoords && (
          <span className="ms-1">
            ({lat.toFixed(6)}, {lon.toFixed(6)})
          </span>
        )}
      </div>
    </div>
  );
}

export default IeLocationMap;
